import { Input, notification, Segmented, Spin } from "antd";
import { CheckOutlined, HistoryOutlined, ReloadOutlined, RobotOutlined, SearchOutlined, SendOutlined, UserOutlined } from "@ant-design/icons";
import { InternalPageLayout } from "components";
import { useAppSelector, usePermissions } from "hooks";
import { UIEvent, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { collection, onSnapshot } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { auth, db } from "config/firebase";

const CHAT_PAGE_SIZE = 10;
const CHAT_SCROLL_THRESHOLD = 48;
const SHOW_UNREAD_BADGE = false;
const SESSION_WINDOW_HOURS = 24;
const REOPEN_TEMPLATE_NAME = "inicio_de_conversacion";
const MESSAGE_SEND_URL = "https://message.goodlook.com.mx/api/message/send";

type StatusFilter = "all" | "active" | "inactive";

interface WhatsAppConversation {
    conversationId: string;
    contactWaId: string;
    displayName: string | null;
    phoneNumber: string;
    lastMessageAt: any;
    sessionExpiresAt: any;
    sessionIsActive: boolean;
    conversationState: string;
    totalMessages: number;
    unreadMessages: number;
}

interface ConversationDetail extends WhatsAppConversation {
    returnedMessages: number;
    skip: number;
    take: number;
    userMessages: any[];
    botMessages: any[];
    messages: any[];
}

// Paleta para los avatares de la lista - se elige una de forma estable segun el
// identificador de la conversacion, solo para diferenciar contactos visualmente.
const AVATAR_PALETTE = [
    { background: "#CFFAFE", color: "#0E7490" },
    { background: "#DCFCE7", color: "#15803D" },
    { background: "#E0F2FE", color: "#0369A1" },
    { background: "#EDE9FE", color: "#6D28D9" },
    { background: "#FEF9C3", color: "#854D0E" },
    { background: "#FCE7F3", color: "#BE185D" }
];

const getAvatarColor = (key: string) => {
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
        hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
    }
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

interface ConversationAvatarProps {
    label: string;
    background: string;
    color: string;
    active: boolean;
    size?: number;
    unreadCount?: number;
}

// Avatar cuadrado con esquinas redondeadas (squircle). El diferenciador principal
// es el borde + punto de estatus: verde si la conversacion esta activa, gris si no.
const ConversationAvatar = ({ label, background, color, active, size = 36, unreadCount = 0 }: ConversationAvatarProps) => {
    const statusColor = active ? "#25D366" : "#BFBFBF";
    const dotSize = Math.max(10, Math.round(size * 0.28));

    return (
        <div style={{ position: "relative", flexShrink: 0, width: size, height: size }}>
            <div
                style={{
                    width: size,
                    height: size,
                    borderRadius: Math.round(size * 0.32),
                    background,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: Math.round(size * 0.34),
                    border: `2px solid ${statusColor}`,
                    boxSizing: "border-box"
                }}
            >
                {label}
            </div>
            <span style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: dotSize,
                height: dotSize,
                borderRadius: "50%",
                background: statusColor,
                border: "2px solid #FFFFFF"
            }} />
            {SHOW_UNREAD_BADGE && unreadCount > 0 && (
                <span style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 999,
                    background: "#FF0072",
                    color: "#FFFFFF",
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: "16px",
                    textAlign: "center",
                    padding: "0 3px",
                    boxShadow: "0 0 0 2px #FFFFFF"
                }}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                </span>
            )}
        </div>
    );
};

const getAvatarInitials = (displayName?: string | null) => {
    const value = displayName?.trim();
    if (!value) return "WA";
    const parts = value.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatDateTime = (value: any) => dayjs(value).format("DD/MM HH:mm");

const formatPhoneNumber = (value?: string | null) => {
    if (!value) return "Sin numero";

    const digits = value.replace(/\D/g, "");
    const localNumber = digits.length === 13 && digits.startsWith("521")
        ? digits.slice(3)
        : digits.length > 10 && digits.startsWith("52")
            ? digits.slice(2)
            : digits;

    if (localNumber.length !== 10) return value;

    return `${localNumber.slice(0, 2)} ${localNumber.slice(2, 6)} ${localNumber.slice(6)}`;
};

// La conversacion se considera activa solo si la ultima interaccion ocurrio hace
// menos de 24 horas - no confiamos unicamente en la bandera que manda el backend.
const isConversationActive = (lastMessageAt: any) => {
    if (!lastMessageAt) return false;
    return dayjs().diff(dayjs(lastMessageAt), "hour", true) < SESSION_WINDOW_HOURS;
};

const normalizeMessage = (message: any) => ({
    body: message.Body ?? message.body ?? "",
    eventTimestamp: normalizeTimestamp(message.EventTimestamp ?? message.eventTimestamp),
    fromMe: message.FromMe ?? message.fromMe,
    messageId: message.MessageId ?? message.messageId,
    messageType: message.MessageType ?? message.messageType,
    mimeType: message.MimeType ?? message.mimeType,
    sender: message.Sender ?? message.sender,
    status: message.Status ?? message.status,
    supportName: message.SupportName ?? message.supportName ?? null,
    waMessageId: message.WaMessageId ?? message.waMessageId
});

const normalizeTimestamp = (value: any) => {
    if (value && typeof value.toDate === "function") return value.toDate();
    if (value?.seconds !== undefined) return new Date(value.seconds * 1000);
    return value;
};

// Junta userMessages + botMessages y los ordena por fecha ascendente (del mas antiguo
// al mas reciente). El bot/usuario solo se usa despues, al pintar, para decidir el lado
// de la burbuja - nunca para el orden.
const mergeMessagesChronologically = (user: any[], bot: any[]) => (
    [...(user || []), ...(bot || [])]
        .filter((message) => message?.eventTimestamp)
        .sort((first, second) => dayjs(first.eventTimestamp).valueOf() - dayjs(second.eventTimestamp).valueOf())
);

const normalizeConversation = (documentId: string, conversation: any): ConversationDetail => {
    const userMessages = (conversation.UserMessages ?? conversation.userMessages ?? []).map(normalizeMessage);
    const botMessages = (conversation.BotMessages ?? conversation.botMessages ?? []).map(normalizeMessage);
    const totalMessages = conversation.TotalMessages ?? conversation.totalMessages ?? Math.max(userMessages.length, botMessages.length);
    const messages = mergeMessagesChronologically(userMessages, botMessages);
    const lastMessageAt = conversation.LastMessageAt ?? conversation.lastMessageAt ?? messages[messages.length - 1]?.eventTimestamp ?? "";
    const phoneNumber = conversation.PhoneNumber ?? conversation.phoneNumber ?? "";
    const maskedPhoneNumber = formatPhoneNumber(phoneNumber);

    return {
        conversationId: conversation.ConversationId ?? conversation.conversationId ?? documentId,
        contactWaId: conversation.ContactWaId ?? conversation.contactWaId ?? "",
        displayName: conversation.DisplayName ?? conversation.displayName ?? maskedPhoneNumber,
        phoneNumber,
        lastMessageAt: normalizeTimestamp(lastMessageAt),
        sessionExpiresAt: normalizeTimestamp(conversation.SessionExpiresAt ?? conversation.sessionExpiresAt),
        sessionIsActive: conversation.SessionIsActive ?? conversation.sessionIsActive ?? false,
        conversationState: conversation.ConversationState ?? conversation.conversationState ?? "",
        totalMessages,
        unreadMessages: conversation.UnreadMessages ?? conversation.unreadMessages ?? 0,
        returnedMessages: conversation.ReturnedMessages ?? conversation.returnedMessages ?? 0,
        skip: conversation.Skip ?? conversation.skip ?? 0,
        take: conversation.Take ?? conversation.take ?? CHAT_PAGE_SIZE,
        userMessages,
        botMessages,
        messages
    };
};

const WhatsAppBot = () => {
    const { can } = usePermissions();
    const canWrite = can("whatsapp", "write");
    const operatorName = useAppSelector(({ user }) => user.data.name);
    const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const [selectedChatTitle, setSelectedChatTitle] = useState<string>("");
    const [selectedChatTitlePhone, setSelectedChatTitlePhone] = useState<string>("");
    const [chatLoading, setChatLoading] = useState(false);
    const [chatLoadingMore, setChatLoadingMore] = useState(false);
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const chatLoaderTimeoutRef = useRef<number | null>(null);
    const [messageText, setMessageText] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [showFreeTextInput, setShowFreeTextInput] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
    const loadingMoreRef = useRef(false);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);
    const conversationDocumentsRef = useRef<ConversationDetail[]>([]);
    const selectedPhoneRef = useRef<string | null>(null);
    const selectedConversationRef = useRef<ConversationDetail | null>(null);
    const selectedConversationDetailRef = useRef<ConversationDetail | null>(null);
    const [panelHeight, setPanelHeight] = useState<number>(window.innerWidth > 1800 ? 750 : 520);

    useEffect(() => {
        const updatePanelHeight = () => {
            setPanelHeight(window.innerWidth > 1800 ? 750 : 520);
        };

        updatePanelHeight();
        window.addEventListener('resize', updatePanelHeight);
        return () => window.removeEventListener('resize', updatePanelHeight);
    }, []);

    useEffect(() => {
        let isMounted = true;
        let unsubscribe: (() => void) | undefined;

        const loadConversations = async () => {
            setIsLoading(true);
            try {
                if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }
                if (!isMounted) return;

                unsubscribe = onSnapshot(
                    collection(db, "conversations"),
                    (snapshot) => {
                        const conversationsData = snapshot.docs.map((document) =>
                            normalizeConversation(document.id, document.data())
                        );
                        conversationDocumentsRef.current = conversationsData;
                        const sorted = [...conversationsData].sort((a, b) =>
                            dayjs(b.lastMessageAt).valueOf() - dayjs(a.lastMessageAt).valueOf()
                        );
                        setConversations(sorted);
                        const isDetailViewActive = Boolean(selectedPhoneRef.current) && Boolean(selectedConversationDetailRef.current);
                        const updatedConversation = conversationsData.find((conversation) =>
                            String(conversation.phoneNumber) === String(selectedPhoneRef.current)
                        );
                        const currentConversation = selectedConversationRef.current;
                        if (!isDetailViewActive && updatedConversation && currentConversation) {
                            const desiredWindow = Math.min(currentConversation.returnedMessages || CHAT_PAGE_SIZE, updatedConversation.messages.length);
                            const start = Math.max(0, updatedConversation.messages.length - desiredWindow);
                            const refreshedConversation = {
                                ...updatedConversation,
                                skip: desiredWindow,
                                returnedMessages: desiredWindow,
                                messages: updatedConversation.messages.slice(start)
                            };
                            selectedConversationRef.current = refreshedConversation;
                            setSelectedConversation(refreshedConversation);
                            setHasMoreMessages(desiredWindow < updatedConversation.messages.length);
                        }
                        setIsLoading(false);
                    },
                    (error) => {
                        console.error("Error escuchando conversaciones:", error);
                        notification.error({
                            message: "Error",
                            description: error.message || "No se pudieron cargar las conversaciones"
                        });
                        setIsLoading(false);
                    }
                );

            } catch (error) {
                console.error("Error cargando conversaciones:", error);
                notification.error({
                    message: "Error",
                    description: "No se pudo cargar el historial de conversaciones"
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConversations();

        return () => {
            isMounted = false;
            unsubscribe?.();
        };
    }, []);

    const filteredConversations = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return conversations.filter((conversation) => {
            const active = isConversationActive(conversation.lastMessageAt);
            if (statusFilter === "active" && !active) return false;
            if (statusFilter === "inactive" && active) return false;
            if (!query) return true;
            const name = (conversation.displayName || "").toLowerCase();
            const phone = (conversation.phoneNumber || "").toLowerCase();
            return name.includes(query) || phone.includes(query);
        });
    }, [conversations, searchQuery, statusFilter]);

    const conversationItems = useMemo(() => {
        return filteredConversations.map((conversation) => {
            const isActive = isConversationActive(conversation.lastMessageAt);
            const isSelected = selectedPhone === conversation.phoneNumber;
            const formattedPhone = formatPhoneNumber(conversation.phoneNumber);
            const title = conversation.displayName?.trim() || formattedPhone || "Sin nombre";
            const unreadCount = conversation.unreadMessages || 0;
            const avatarColor = getAvatarColor(conversation.conversationId || conversation.phoneNumber || title);
            const lastMessage: any = conversationDocumentsRef.current
                .find((item) => item.conversationId === conversation.conversationId)
                ?.messages?.slice(-1)[0];

            return (
                <div
                    key={conversation.conversationId}
                    onClick={() => {
                        setSelectedPhone(conversation.phoneNumber);
                        selectedPhoneRef.current = conversation.phoneNumber;
                        setSelectedChatTitle(title);
                        setSelectedChatTitlePhone(formattedPhone);
                        setSelectedConversation(null);
                        setShowFreeTextInput(false);
                        setIsMessagesLoading(true);
                    }}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 16px",
                        borderBottom: "1px solid #F0F0F0",
                        borderLeft: isSelected ? "3px solid #FF0072" : "3px solid transparent",
                        background: isSelected ? "#FFF0F6" : "#FFFFFF",
                        cursor: "pointer"
                    }}
                >
                    <ConversationAvatar
                        label={getAvatarInitials(title)}
                        background={avatarColor.background}
                        color={avatarColor.color}
                        active={isActive}
                        unreadCount={unreadCount}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: "#1F2328", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {title}
                            </span>
                            <span style={{ fontSize: 10, color: isSelected ? "#FF0072" : "#8C8C8C", flexShrink: 0 }}>
                                {formatDateTime(conversation.lastMessageAt)}
                            </span>
                        </div>
                        {lastMessage?.body && (
                            <div style={{ fontSize: 11, color: "#8C8C8C", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                                {lastMessage.body}
                            </div>
                        )}
                        <div style={{ fontSize: 11, color: "#8C8C8C", marginTop: 2 }}>
                            Total interacciones: <strong>{conversation.totalMessages}</strong>
                        </div>
                    </div>
                </div>
            );
        });
    }, [filteredConversations, selectedPhone]);

    useEffect(() => {
        if (!selectedPhone) {
            setSelectedConversation(null);
            selectedConversationRef.current = null;
            selectedConversationDetailRef.current = null;
            setChatLoading(false);
            setIsMessagesLoading(false);
            return;
        }

        if (chatLoaderTimeoutRef.current) {
            window.clearTimeout(chatLoaderTimeoutRef.current);
        }

        setIsMessagesLoading(true);
        setSelectedConversation(null);

        const scrollToBottom = () => {
            requestAnimationFrame(() => {
                if (!chatScrollRef.current) return;
                chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
            });
        };

        let unsubscribe: (() => void) | undefined;

        const loadByPhone = () => {
            setChatLoading(true);
            setChatLoadingMore(false);
            setIsMessagesLoading(true);
            setHasMoreMessages(false);
            loadingMoreRef.current = false;
            setSelectedConversation(null);
            selectedConversationRef.current = null;
            selectedConversationDetailRef.current = null;

            unsubscribe = onSnapshot(
                collection(db, "conversations-by-phone"),
                (snapshot) => {
                    const matchingConversation = snapshot.docs.find((document) => {
                        const data = document.data();
                        const phoneValue = data.PhoneNumber ?? data.phoneNumber ?? document.id;
                        return String(phoneValue) === String(selectedPhone) || String(document.id) === String(selectedPhone);
                    });

                    if (!matchingConversation) {
                        setSelectedConversation(null);
                        selectedConversationRef.current = null;
                        selectedConversationDetailRef.current = null;
                        setChatLoading(false);
                        setIsMessagesLoading(false);
                        return;
                    }

                    const data = normalizeConversation(matchingConversation.id, matchingConversation.data());
                    selectedConversationDetailRef.current = data;

                    const totalMessages = data.messages.length;

                    // Conserva la cantidad de mensajes que ya estaban cargados (en vez de
                    // volver siempre a la primera pagina) para que enviar/recibir un mensaje
                    // no trunque el historial que el usuario ya habia cargado con "cargar mas".
                    // La ventana visible siempre se toma del FINAL de la lista ya ordenada
                    // cronologicamente, para garantizar que el ultimo mensaje real quede incluido
                    // incluso en conversaciones largas.
                    const previousConversation = selectedConversationRef.current;
                    const desiredWindow = Math.min(
                        Math.max(previousConversation?.returnedMessages || CHAT_PAGE_SIZE, CHAT_PAGE_SIZE),
                        totalMessages
                    );
                    const windowStart = Math.max(0, totalMessages - desiredWindow);

                    const chatContainer = chatScrollRef.current;
                    const wasNearBottom = !chatContainer || !previousConversation || (
                        chatContainer.scrollHeight - chatContainer.scrollTop - chatContainer.clientHeight <= 80
                    );

                    const nextConversation = {
                        ...data,
                        skip: desiredWindow,
                        returnedMessages: desiredWindow,
                        messages: data.messages.slice(windowStart)
                    };

                    selectedConversationRef.current = nextConversation;
                    setSelectedConversation(nextConversation);
                    setHasMoreMessages(desiredWindow < totalMessages);
                    setChatLoading(false);

                    chatLoaderTimeoutRef.current = window.setTimeout(() => {
                        setIsMessagesLoading(false);
                    }, 2000);

                    // Solo forzamos el scroll al final si el usuario ya estaba viendo los
                    // mensajes mas recientes (o es la primera carga); si habia subido a leer
                    // historial no lo regresamos abajo de golpe.
                    if (wasNearBottom) {
                        requestAnimationFrame(() => {
                            if (chatScrollRef.current) {
                                chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
                            }
                        });
                    }
                },
                (error) => {
                    console.error("Error escuchando conversacion por telefono:", error);
                    notification.error({
                        message: "Error",
                        description: "No se pudo cargar la conversacion seleccionada"
                    });
                    setChatLoading(false);
                    setIsMessagesLoading(false);
                }
            );
        };

        setShowFreeTextInput(false);
        loadByPhone();
        scrollToBottom();

        return () => unsubscribe?.();
    }, [selectedPhone]);

    const loadMoreMessages = async () => {
        if (!selectedPhone || !selectedConversation || !hasMoreMessages || loadingMoreRef.current) return;

        loadingMoreRef.current = true;
        setChatLoadingMore(true);

        try {
            const data = selectedConversationDetailRef.current;
            if (!data) throw new Error("No se encontro la conversacion seleccionada");

            const totalMessages = data.messages.length;
            const currentConversation = selectedConversationRef.current;
            const desiredWindow = Math.min((currentConversation?.returnedMessages || 0) + CHAT_PAGE_SIZE, totalMessages);
            const windowStart = Math.max(0, totalMessages - desiredWindow);

            const nextConversation = currentConversation ? {
                ...currentConversation,
                skip: desiredWindow,
                returnedMessages: desiredWindow,
                messages: data.messages.slice(windowStart)
            } : null;
            selectedConversationRef.current = nextConversation;
            setSelectedConversation(nextConversation);
            setHasMoreMessages(desiredWindow < totalMessages);
        } catch (error) {
            console.error(error);
            notification.error({ message: 'Error', description: 'No se pudieron cargar mas mensajes' });
        } finally {
            loadingMoreRef.current = false;
            setChatLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!selectedConversation || isMessagesLoading) return;
        requestAnimationFrame(() => {
            if (!chatScrollRef.current) return;
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        });
    }, [selectedConversation, isMessagesLoading]);

    const submitToMessageApi = async (body: Record<string, any>) => {
        const response = await fetch(MESSAGE_SEND_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });

        const responseText = await response.text();
        let payload: any = null;
        try {
            payload = responseText ? JSON.parse(responseText) : null;
        } catch {
            payload = null;
        }

        if (!response.ok) {
            throw new Error(payload?.message || payload?.error || `Error HTTP ${response.status}`);
        }
    };

    const sendMessage = async () => {
        const trimmedMessage = messageText.trim();
        if (!selectedPhone || !trimmedMessage || sendingMessage) return;

        setSendingMessage(true);

        try {
            await submitToMessageApi({
                phone: String(selectedPhone),
                message: trimmedMessage,
                template: null,
                language_code: null,
                supportActive: true,
                supportName: operatorName || "Operador"
            });

            setMessageText("");
            notification.success({
                message: "Mensaje enviado",
                description: "El mensaje se envio correctamente."
            });
        } catch (error) {
            console.error("Error enviando mensaje:", error);
            notification.error({
                message: "Error",
                description: error instanceof Error ? error.message : "No se pudo enviar el mensaje"
            });
        } finally {
            setSendingMessage(false);
        }
    };

    const sendReopenTemplate = async () => {
        if (!selectedPhone || sendingMessage) return;

        setSendingMessage(true);

        try {
            await submitToMessageApi({
                phone: String(selectedPhone),
                template: REOPEN_TEMPLATE_NAME,
                language_code: "es",
                supportActive: true,
                supportName: operatorName || "Operador",
                template_parameters: [{ type: "text", text: operatorName || "Operador" }]
            });

            notification.success({
                message: "Conversacion reabierta",
                description: "Se envio la plantilla para reactivar la conversacion."
            });
        } catch (error) {
            console.error("Error reabriendo conversacion:", error);
            notification.error({
                message: "Error",
                description: error instanceof Error ? error.message : "No se pudo reactivar la conversacion"
            });
        } finally {
            setSendingMessage(false);
        }
    };

    const isBotMessage = (message: any) => {
        const sender = String(message?.sender ?? "").toLowerCase();
        return Boolean(message?.fromMe) || sender === "business" || sender === "bot";
    };

    const renderChatView = ({ conversation, chatTitle, selectedChatTitlePhone }: { conversation: ConversationDetail; chatTitle: string; selectedChatTitlePhone: string }) => {
        const avatarColor = getAvatarColor(conversation.conversationId || conversation.phoneNumber || chatTitle);
        const active = isConversationActive(conversation.lastMessageAt);

        return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 16, borderBottom: "1px solid #F0F0F0" }}>
                <ConversationAvatar
                    label={getAvatarInitials(chatTitle)}
                    background={avatarColor.background}
                    color={avatarColor.color}
                    active={active}
                    size={44}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1F2328" }}>{chatTitle}</div>
                    <div style={{ fontSize: 13, color: "#8C8C8C" }}>{selectedChatTitlePhone}</div>
                </div>
            </div>

            <div
                ref={chatScrollRef}
                onScroll={(event: UIEvent<HTMLDivElement>) => {
                    const element = event.currentTarget;
                    if (element.scrollTop <= CHAT_SCROLL_THRESHOLD) loadMoreMessages();
                }}
                style={{ flex: 1, overflowY: "auto", padding: "16px 4px", display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}
            >
                {chatLoading ? (
                    <div style={{ textAlign: "center" }}><Spin spinning tip="Cargando chat..." /></div>
                ) : (
                    <>
                    {chatLoadingMore && <div style={{ textAlign: "center" }}><Spin size="small" tip="Cargando mensajes anteriores..." /></div>}
                    {(conversation.messages || []).map((m: any, idx: number) => {
                        const isBot = isBotMessage(m);
                        const isUser = !isBot;
                        const isOperator = isBot && Boolean(m.supportName);
                        const bubbleBackground = isUser ? '#FFFFFF' : (isOperator ? '#DCF8C6' : '#F3E8FF');
                        const borderColor = isUser ? '#E8E0D5' : (isOperator ? '#CDE7B0' : '#E9D5FF');
                        const tagLabel = isUser ? 'Cliente' : (isOperator ? m.supportName : 'Asistente Virtual');

                        return (
                            <div key={m.messageId || idx} style={{ display: "flex", flexDirection: "column", alignItems: isUser ? 'flex-start' : 'flex-end' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8C8C8C', margin: '0 4px 2px' }}>
                                    {isOperator ? <UserOutlined /> : (isUser ? null : <RobotOutlined />)}
                                    <span>{tagLabel}</span>
                                </div>
                                <div style={{
                                    maxWidth: '70%',
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    background: bubbleBackground,
                                    border: `1px solid ${borderColor}`,
                                    color: '#111',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                                    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-start' : 'flex-end', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: '#666' }}>
                                        <span>{dayjs(m.eventTimestamp).format('hh:mm A')}</span>
                                        {isBot && m.status && <CheckOutlined style={{ color: m.status === 'read' ? '#1890ff' : '#999', fontSize: 12 }} />}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </>
                )}
            </div>
            <div style={{ paddingTop: 12, borderTop: '1px solid #F0F0F0', marginTop: 12 }}>
                {canWrite && (!active && !showFreeTextInput ? (
                    <div style={{ background: '#FFF8E6', border: '1px solid #F5E6B8', borderRadius: 14, padding: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1F2328' }}>Conversacion inactiva</div>
                        <div style={{ fontSize: 12, color: '#8C8C8C', marginBottom: 10 }}>
                            Selecciona una plantilla predeterminada o escribe un mensaje libre.
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                                onClick={sendReopenTemplate}
                                disabled={sendingMessage}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 14px', borderRadius: 999, border: '1px solid #E8E0D5',
                                    background: '#FFFFFF', color: '#1F2328', fontSize: 13,
                                    cursor: sendingMessage ? 'not-allowed' : 'pointer', opacity: sendingMessage ? 0.7 : 1
                                }}
                            >
                                {sendingMessage ? <Spin size="small" /> : <ReloadOutlined />}
                                Reactivar conversacion
                            </button>
{   /*                         <button
                                onClick={() => setShowFreeTextInput(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 14px', borderRadius: 999, border: 'none',
                                    background: '#FF0072', color: '#FFFFFF', fontSize: 13, cursor: 'pointer'
                                }}
                            >
                                <EditOutlined />
                                Escribir mensaje libre
                            </button>*/}
                        </div>
                    </div>
                ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Spin spinning={sendingMessage} size="small" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>
                            <textarea
                                value={messageText}
                                onChange={(event) => setMessageText(event.target.value)}
                                placeholder="Escribe un mensaje..."
                                disabled={sendingMessage}
                                style={{
                                    width: '100%',
                                    minHeight: 56,
                                    resize: 'none',
                                    borderRadius: 20,
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    padding: '12px 40px 12px 16px',
                                    background: '#FFFFFF',
                                    color: '#2F2F2F',
                                    outline: 'none',
                                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                                    fontSize: 14,
                                    lineHeight: 1.4,
                                    boxSizing: 'border-box'
                                }}
                            />
                        </Spin>
                    </div>
                    <button
                        onClick={sendMessage}
                        disabled={!selectedPhone || !messageText.trim() || sendingMessage}
                        style={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            border: 'none',
                            background: !selectedPhone || !messageText.trim() || sendingMessage ? '#D8D8D8' : '#FF0072',
                            color: '#FFFFFF',
                            cursor: !selectedPhone || !messageText.trim() || sendingMessage ? 'not-allowed' : 'pointer',
                            boxShadow: !selectedPhone || !messageText.trim() || sendingMessage ? 'none' : '0 4px 10px rgba(255, 0, 114, 0.25)',
                            transition: 'all 0.2s ease',
                            opacity: sendingMessage ? 0.8 : 1
                        }}
                        title="Enviar mensaje"
                    >
                        <SendOutlined style={{ fontSize: 18 }} />
                    </button>
                </div>
                ))}
            </div>
        </div>
        );
    };

    return (
        <InternalPageLayout title="Bitacora Bot">
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{
                    height: panelHeight,
                    minWidth: 320,
                    background: "#FFFFFF",
                    borderRadius: 20,
                    boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                }}>
                    <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #F0F0F0" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#1F2328", letterSpacing: 0.3 }}>
                                <HistoryOutlined style={{ color: "#FF0072" }} />
                                HISTORIAL POR ULTIMA INTERACCION
                            </div>
                            <span style={{ background: "#FFF0F6", color: "#FF0072", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                {conversations.length} chats
                            </span>
                        </div>
                        <Input
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Buscar por cliente o telefono..."
                            prefix={<SearchOutlined style={{ color: "#BFBFBF" }} />}
                            allowClear
                            style={{ borderRadius: 999, marginBottom: 10 }}
                        />
                        <Segmented
                            block
                            value={statusFilter}
                            onChange={(value) => setStatusFilter(value as StatusFilter)}
                            options={[
                                { label: "Todos", value: "all" },
                                { label: "Activas", value: "active" },
                                { label: "Inactivas", value: "inactive" }
                            ]}
                        />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', background: "#FFFFFF" }}>
                        <Spin spinning={isLoading} tip="Cargando conversaciones...">
                            {conversationItems.length > 0 ? (
                                conversationItems
                            ) : (
                                <div style={{ padding: 40, textAlign: "center", color: "#8C8C8C" }}>
                                    {isLoading ? null : <span>No hay conversaciones disponibles.</span>}
                                </div>
                            )}
                        </Spin>
                    </div>
                </div>

                <div style={{
                    flex: 1,
                    minWidth: 320,
                    background: "#FFFFFF",
                    borderRadius: 20,
                    display: "flex",
                    flexDirection: "column",
                    padding: 24,
                    height: panelHeight,
                    color: "#1F2328",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.05)"
                }}>
                    {selectedPhone && (!selectedConversation || isMessagesLoading) ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <Spin size="large" tip="Cargando mensajes..." />
                            </div>
                        </div>
                    ) : selectedConversation ? (
                        renderChatView({ conversation: selectedConversation, chatTitle: selectedChatTitle, selectedChatTitlePhone: selectedChatTitlePhone })
                    ) : (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                            <div style={{textAlign: 'center'}}>
                                <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Selecciona una conversacion</p>
                                <p style={{ marginTop: 12, color: "#8C8C8C" }}>Aqui aparecera el contenido cuando elijas un chat.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InternalPageLayout>
    );
};

export default WhatsAppBot;
